import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { MdInfoOutline } from 'react-icons/md';
import { TERM_DEFINITIONS } from './termDefinitions';

import styles from './InfoPopover.module.css';

const VIEWPORT_MARGIN = 8;
const TRIGGER_GAP = 6;

// Place the note beside its trigger: below by default, above when it would overflow the
// bottom, and always shifted back inside the viewport.
const placeNear = (trigger, note) => {
    const rect = trigger.getBoundingClientRect();
    const width = note.offsetWidth;
    const height = note.offsetHeight;
    let top = rect.bottom + TRIGGER_GAP;
    if (top + height > window.innerHeight - VIEWPORT_MARGIN) top = rect.top - TRIGGER_GAP - height;
    top = Math.max(VIEWPORT_MARGIN, top);
    const left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN)
    );
    return { top: `${top}px`, left: `${left}px` };
};

// Click/keyboard-activated explanation for a metric label. The content is a plain note
// (not a dialog), so focus is never moved or trapped; Escape or an outside click closes it.
export default function InfoPopover({ term, description = TERM_DEFINITIONS[term] }) {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState(null);
    const wrapperRef = useRef(null);
    const triggerRef = useRef(null);
    const noteRef = useRef(null);
    const contentId = useId();

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = event => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener('click', onPointerDown);
        return () => document.removeEventListener('click', onPointerDown);
    }, [open]);

    useLayoutEffect(() => {
        if (!open) return undefined;
        const update = () => {
            if (triggerRef.current && noteRef.current) {
                setPosition(placeNear(triggerRef.current, noteRef.current));
            }
        };
        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [open]);

    return (
        <span
            ref={wrapperRef}
            className={styles.wrapper}
            onKeyDown={event => {
                if (event.key === 'Escape' && open) {
                    event.stopPropagation();
                    setOpen(false);
                }
            }}
        >
            <button
                type='button'
                ref={triggerRef}
                className={styles.trigger}
                aria-label={`About ${term}`}
                aria-expanded={open}
                aria-controls={contentId}
                onClick={() => setOpen(current => !current)}
            >
                <MdInfoOutline aria-hidden='true' focusable='false' />
            </button>
            {open && (
                <span
                    id={contentId}
                    ref={noteRef}
                    role='note'
                    className={styles.popover}
                    style={position || { visibility: 'hidden' }}
                >
                    <strong data-term-title className={styles.title}>
                        {term}
                    </strong>
                    <span data-term-body className={styles.body}>
                        {description}
                    </span>
                </span>
            )}
        </span>
    );
}
